import { assert, Logger } from '@deqo/foundation';
import { injectable } from 'inversify';
import { ApiClient } from '../../../api/ApiClient';
import { ObjectManager } from '../../../concepts/objects';
import { Camera } from '../../canvas/Camera';
import { Physicals } from '../../canvas/entities/Physicals';
import { ContentPayload } from '../../canvas/injection/ContentPayload';
import { ContentProcessor } from '../../canvas/injection/ContentProcessor';
import { resolve, Resolver } from '../../Container';
import { Graphic, LocalUrlAttr } from './Graphic';
import { clampDimensions, getDimensions } from './GraphicUtil';

const errLog = Logger.get('app.errors');
const updateKey = (id: string) => 'create:${}';

@injectable()
export class GraphicFromImageFileProcessor implements ContentProcessor {

    @resolve(ApiClient) api!: ApiClient;
    @resolve(Camera) camera!: Camera;
    @resolve(ObjectManager) objects!: ObjectManager;
    @resolve(Resolver) resolver!: Resolver;

    public readonly supportedTypes = Object.freeze([
        'image/apng',
        'image/gif',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/svg+xml',
        'image/webp',
    ]);

    public async process(payload: ContentPayload): Promise<boolean | ContentPayload> {
        const { api, camera, objects, resolver } = this;
        const { data, type, metadata } = payload;

        // Specifically SVG can be a string, if it is a string, convert it to a blob and resubmit for processing
        if (type === 'image/svg+xml' && typeof (data) === 'string') {
            return ContentPayload.derive(payload, type, new Blob([data], { type }), metadata);
        }

        // Check is file or blob
        assert(data instanceof File || data instanceof Blob, 'ImageFileHandler: input must be a File or Blob.');
        const dataUrl = URL.createObjectURL(data);

        try {
            const [x, y] = metadata?.position?.toArray() ?? [];
            const [w, h] = clampDimensions(camera, await getDimensions(data));

            // Create the graphic without a URL, use the local data URL for this user (others will see the placeholder styling)
            const graphic = objects.update(u => {
                const inst = Physicals.create(resolver, Graphic, [x, y, w, h], nc => LocalUrlAttr.set(nc, dataUrl));
                u.set('key', `edit:${inst.id}`);
                return inst;
            });

            // Upload the image, could take a few seconds
            const { url } = await api.upload('/files', data);

            // Update the graphic with the uploaded URL and remove the local override
            objects.update(u => {
                u.set('key', `edit:${graphic.id}`);
                graphic.url = url;
                LocalUrlAttr.set(graphic, null);
            });

            return true;
        }
        catch (e) {
            errLog.error(e);
            return false;
        }
    }

}

